import { useState, useCallback, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from 'react'
import {
    Form,
    Input,
    Select,
    Radio,
    Button,
    Steps,
    Row,
    Col,
    Divider,
    TextArea,
    Search,
    Checkbox,
    DatePicker,
    InputNumber,
    message,
} from 'gokwik-ui-kit'
import { Field, JsonDrivenFormWizardProps, Section, FormWizardRef, Condition, CompositeCondition } from './types'
import './form-wizard.css'

export const BulletedTextArea = ({ value, onChange, placeholder, ...props }: any) => {
    const contentEditableRef = useRef<HTMLDivElement>()

    useEffect(() => {
        contentEditableRef.current.innerHTML =
            value
                ?.split('\n')
                ?.map((line, i) => (i === 0 ? line : `<div>${line}</div>`))
                ?.join('') || ''
    }, [])

    const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
        const content = e.currentTarget.innerText?.trimEnd()
        onChange?.(content)
    }

    const handlePaste = (e) => {
        e.preventDefault()
        const text = (e.originalEvent || e).clipboardData
            .getData('text/plain')
            ?.split(/\r*\n/)
            ?.filter((t) => t?.trim())
            ?.join('\n')
        document.execCommand('insertText', false, text)
    }

    return (
        <div className='bulletTextAreaWrapper p-1 border border-solid border-gray-300 rounded-lg'>
            <div
                ref={contentEditableRef}
                contentEditable
                onInput={handleInput}
                onPaste={handlePaste}
                placeholder={placeholder}
                className='bulletTextArea min-h-[120px] outline-none whitespace-pre-wrap list-item list-disc [&_div]:list-item [&_div]:list-disc ml-4'
                {...props}
            ></div>
        </div>
    )
}

const JsonDrivenFormWizard = forwardRef<FormWizardRef, JsonDrivenFormWizardProps>(
    (
        {
            formSchema,
            onFinish,
            onFormFieldsChange,
            onFormValuesChange,
            onStepChange,
            form: externalForm,
            initialValues,
            hideNavigationButtons,
            moveToLatest,
            freeForm,
            step,
        },
        ref,
    ) => {
        const form = externalForm || Form.useForm()[0]
        const [currentStep, setCurrentStep] = useState(step || 0)
        const [maxStepVisited, setMaxStepVisited] = useState(0)
        const [, forceUpdate] = useState({})

        // Create a map of fields that are conditions for other fields
        const conditionalFieldsMap = useMemo(
            () =>
                formSchema.steps
                    .flatMap((step) => step.sections.flatMap((section) => section?.fields || []))
                    .reduce((acc, field) => {
                        if (!field.condition) return acc
                        const rootConditions = (field.condition as CompositeCondition).conditions
                            ? (field.condition as CompositeCondition).conditions
                            : [field.condition as Condition]
                        const pushConditions = (conditions: Array<Condition | CompositeCondition>) => {
                            conditions.forEach((condition) => {
                                if ((condition as CompositeCondition).conditions) {
                                    pushConditions((condition as CompositeCondition).conditions)
                                    return
                                }
                                condition = condition as Condition
                                const { field: conditionField, value: conditionValue } = condition
                                if (!acc[conditionField]) {
                                    acc[conditionField] = []
                                }
                                acc[conditionField].push({ field, expectedValue: conditionValue })
                            })
                        }
                        pushConditions(rootConditions)
                        return acc
                    }, {} as Record<string, { field: Field; expectedValue: string | number | boolean | unknown[] }[]>),
            [formSchema],
        )

        const runCondition = (condition: Condition | CompositeCondition, values: Record<string, any>): boolean => {
            if ((condition as CompositeCondition).operation) {
                condition = condition as CompositeCondition
                return validateConditions(condition.conditions, values, condition.operation)
            }
            condition = condition as Condition
            switch (condition.operator) {
                case 'in':
                    return typeof condition.value === 'string' || Array.isArray(condition.value)
                        ? condition.value.includes(values[condition.field])
                        : false
                case 'not':
                    return values[condition.field] !== condition.value

                case 'equals':
                default:
                    return values[condition.field] === condition.value
            }
        }

        const validateConditions = (
            conditions: (Condition | CompositeCondition)[],
            values: Record<string, any>,
            operation: CompositeCondition['operation'],
        ): boolean => {
            if (!conditions?.length) return true
            switch (operation) {
                case 'every':
                    return conditions.every((c) => runCondition(c, values))

                case 'some':
                    return conditions.some((c) => runCondition(c, values))
            }
        }

        const checkConditions = (field: Field, values: Record<string, any>) => {
            if (!field.condition) return true

            const conditions = (field.condition as CompositeCondition).conditions
                ? (field.condition as CompositeCondition).conditions
                : [field.condition as Condition]
            const operation = (field.condition as CompositeCondition).operation || 'every'

            return validateConditions(conditions, values, operation)
        }

        const handleValuesChange = useCallback(
            (changedValues: Record<string, any>, allValues: Record<string, any>) => {
                let shouldForceUpdate = false

                // Only process if there are actual changes
                if (Object.keys(changedValues).length > 0) {
                    onFormValuesChange?.(allValues)

                    Object.keys(changedValues).forEach((changedField) => {
                        const dependentFields = conditionalFieldsMap[changedField]
                        if (dependentFields) {
                            shouldForceUpdate = true
                            dependentFields.forEach(({ field }) => {
                                // Check all conditions for the field
                                if (!checkConditions(field, allValues)) {
                                    // Clear the dependent field's value if any condition is not met
                                    form.setFields([
                                        {
                                            name: field.name,
                                            value: undefined,
                                        },
                                    ])
                                }
                            })
                        }
                    })

                    // Force re-render only if condition fields changed
                    if (shouldForceUpdate) {
                        forceUpdate({})
                    }
                }
            },
            [form, conditionalFieldsMap],
        )

        // Set initial values and handle conditional fields
        useEffect(() => {
            if (initialValues && Object.keys(initialValues).length > 0) {
                // Set values without triggering onChange
                form.setFieldsValue(initialValues)
                // Process initial conditions without triggering the API call
                Object.keys(conditionalFieldsMap).forEach((field) => {
                    if (field in initialValues) {
                        const dependentFields = conditionalFieldsMap[field]
                        dependentFields.forEach(({ field: dependentField }) => {
                            if (!checkConditions(dependentField, initialValues)) {
                                form.setFields([
                                    {
                                        name: dependentField.name,
                                        value: undefined,
                                    },
                                ])
                            }
                        })
                    }
                })

                if (!freeForm) {
                    setCurrentStep(0)
                    moveToLatestFn()
                }
            }
        }, [])

        const moveToLatestFn = async (i = 0) => {
            try {
                if (i >= formSchema.steps.length) return
                await form.validateFields({ validateOnly: true })
                if (moveToLatest) {
                    setCurrentStep(i)
                    onStepChange?.(i)
                }
                setMaxStepVisited(Math.max(maxStepVisited, i))
                moveToLatestFn(i + 1)
            } catch (_) {}
        }

        const handleStepClick = async (step: number) => {
            if (freeForm) {
                setCurrentStep(step)
                onStepChange?.(step)
                return
            }
            if (step <= maxStepVisited || step === currentStep + 1) {
                if (step > currentStep) {
                    try {
                        const currentStepFields = formSchema.steps[currentStep].sections.flatMap(
                            (section) => section.fields,
                        )
                        await form.validateFields(currentStepFields.map((f) => f.name))
                        setCurrentStep(step)
                        setMaxStepVisited(Math.max(maxStepVisited, step))
                        onStepChange?.(step)
                    } catch (error) {
                        console.log('Validation failed in handleStepClick:', error)
                    }
                } else {
                    setCurrentStep(step)
                    onStepChange?.(step)
                }
            }
        }

        const handleNext = async () => {
            try {
                const currentStepFields = formSchema.steps[currentStep].sections.flatMap((section) => section.fields)
                await form.validateFields(currentStepFields.map((f) => f.name))
                const nextStep = currentStep + 1
                if (nextStep >= formSchema.steps.length) {
                    handleSubmit?.()
                    return
                }
                setCurrentStep(nextStep)
                setMaxStepVisited(Math.max(maxStepVisited, nextStep))
                onStepChange?.(nextStep)
            } catch (error) {
                console.log('Validation failed:', error)
                message.error('Validation failed!')
            }
        }

        const handlePrev = () => {
            const prevStep = currentStep - 1
            setCurrentStep(prevStep)
            onStepChange?.(prevStep)
        }

        const handleSubmit = async () => {
            try {
                await form.validateFields()
                const allValues = form.getFieldsValue(true)
                const flattenedForm = formSchema.steps
                    .flatMap((step) => step.sections)
                    .flatMap((section) => section.fields)
                // Filter out values from conditional fields whose conditions are not met
                const filteredValues = Object.fromEntries(
                    Object.entries(allValues).filter(([fieldName]) => {
                        const field = flattenedForm.find((f) => f.name === fieldName)
                        if (!field) return true
                        return checkConditions(field, allValues)
                    }),
                )
                onFinish?.(filteredValues)
            } catch (error) {
                console.log('Validation failed:', error)
            }
        }

        const renderField = (field: Field) => {
            switch (field.type) {
                case 'custom':
                case 'custom_direct':
                    return field.render(field.inputProps)
                case 'input':
                    switch (field.subType) {
                        case 'textarea':
                            return <TextArea placeholder={field.placeholder} {...field.inputProps} />

                        case 'bulletTextArea':
                            return <BulletedTextArea placeholder={field.placeholder} {...field.inputProps} />

                        case 'password':
                            return <Input.Password placeholder={field.placeholder} {...field.inputProps} />

                        case 'search':
                            return <Search placeholder={field.placeholder} {...field.inputProps} />

                        case 'number':
                            return <InputNumber placeholder={field.placeholder} {...field.inputProps} />

                        default:
                            return <Input placeholder={field.placeholder} {...field.inputProps} />
                    }

                case 'select':
                    const { suffixIcon, ...restInputProps } = field.inputProps || {}
                    return (
                        <Select placeholder={field.placeholder} {...restInputProps} suffixIcon={suffixIcon as never}>
                            {field.options?.map((option) => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    )

                case 'radio':
                    return <Radio.Group options={field.options} {...field.inputProps} />

                case 'checkbox':
                    return <Checkbox {...field.inputProps}>{field.label}</Checkbox>

                case 'checkboxGroup':
                    return <Checkbox.Group options={field.options} {...field.inputProps} />

                case 'date':
                    return <DatePicker {...field.inputProps} />

                case 'dateRange':
                    return <DatePicker.RangePicker {...field.inputProps} />

                default:
                    return null
            }
        }

        const shouldRenderField = (field: Field) => {
            if (!field.condition) return true
            return checkConditions(field, form.getFieldsValue(true))
        }

        const renderSection = useCallback(
            (section: Section, sectionIndex: number) => {
                let currentRow = []
                const rows = []
                let currentSpan = 0

                // Process fields into rows
                section?.fields?.forEach((field: Field, index: number) => {
                    if (shouldRenderField(field)) {
                        const fieldSpan = field.layout?.span || 24
                        const fieldOffset = field.layout?.offset || 0

                        // If this field would exceed row width, push current row and start new one
                        if (currentSpan + fieldSpan + fieldOffset > 24) {
                            rows.push(currentRow)
                            currentRow = []
                            currentSpan = 0
                        }

                        currentRow.push({
                            field,
                            span: fieldSpan,
                            offset: fieldOffset,
                            isLast: index === section.fields.length - 1,
                        })
                        currentSpan += fieldSpan + fieldOffset
                    }
                })

                // Push the last row if it has fields
                if (currentRow.length > 0) {
                    rows.push(currentRow)
                }

                // Only render the section if it has visible fields
                const hasVisibleFields = rows.length > 0
                const nextSections = formSchema.steps[currentStep].sections.slice(sectionIndex + 1)
                const nextSectionHasVisibleFields = nextSections?.some((section) =>
                    section?.fields?.some((field) => shouldRenderField(field)),
                )

                return hasVisibleFields ? (
                    <div key={sectionIndex} className='flex flex-col gap-y-2'>
                        {section.title && <h3 className='my-0 text-lg font-medium'>{section.title}</h3>}
                        {section.description && <p className='text-gray-500'>{section.description}</p>}
                        {rows.map((row, rowIndex) => (
                            <Row key={rowIndex} gutter={8}>
                                {row.map(({ field, span, offset, isLast }, i) => (
                                    <Col key={field.name || i} span={span} offset={offset}>
                                        {field.type === 'custom_direct' ? (
                                            renderField(field)
                                        ) : (
                                            <Form.Item
                                                className={(isLast ? 'mb-0' : 'mb-2') + ' form-item-custom-validation'}
                                                name={field.name}
                                                label={field.type === 'checkbox' ? null : field.label}
                                                rules={field.rules}
                                                tooltip={field.tooltip}
                                                {...(field.type === 'checkbox' ? { valuePropName: 'checked' } : {})}
                                            >
                                                {renderField(field)}
                                            </Form.Item>
                                        )}
                                    </Col>
                                ))}
                            </Row>
                        ))}
                        {sectionIndex < formSchema.steps[currentStep].sections.length - 1 &&
                            nextSectionHasVisibleFields && <Divider className='my-2' />}
                    </div>
                ) : null
            },
            [formSchema, currentStep],
        )

        useImperativeHandle(
            ref,
            () => ({
                next: handleNext,
                prev: handlePrev,
                form,
                currentStep,
            }),
            [currentStep, form],
        )

        return (
            <Form
                form={form}
                layout='vertical'
                onFinish={handleSubmit}
                requiredMark='optional'
                onValuesChange={handleValuesChange}
                onFieldsChange={onFormFieldsChange}
                initialValues={initialValues}
                scrollToFirstError
            >
                <Steps
                    current={currentStep}
                    items={formSchema.steps.map((step, index) => ({
                        title: step.title,
                        disabled: !freeForm && index > maxStepVisited,
                    }))}
                    onChange={handleStepClick}
                    {...formSchema.stepsProps}
                />

                <div className='mt-8'>
                    {formSchema.steps[currentStep].sections.map((section, index) => renderSection(section, index))}
                </div>

                {!hideNavigationButtons && (
                    <div className='flex gap-x-2 mt-8'>
                        {currentStep > 0 && <Button onClick={handlePrev}>Previous</Button>}
                        {currentStep < formSchema.steps.length - 1 && (
                            <Button type='primary' onClick={handleNext}>
                                Next
                            </Button>
                        )}
                        {currentStep === formSchema.steps.length - 1 && (
                            <Button type='primary' htmlType='submit'>
                                Submit
                            </Button>
                        )}
                    </div>
                )}
            </Form>
        )
    },
)

JsonDrivenFormWizard.displayName = 'JsonDrivenFormWizard'

export default JsonDrivenFormWizard
